import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import HistoryIcon from "@mui/icons-material/History";
import ReplayIcon from "@mui/icons-material/Replay";
import { MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Chip,
	IconButton,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useState } from "react";

import { MechanicalTestRunDisposition } from "../../models/MechanicalTestRunDisposition";
import {
	disposedByLabel,
	formatDate,
	formatTime,
	formatTimestamp,
} from "./format";
import { Disposition } from "./types";

const iconChipSx = { height: 20, width: 26, "& .MuiChip-label": { px: 0 } };
const chipIconSx = { fontSize: 13, display: "block" };

export const StatusChip = ({
	code,
}: {
	code: Disposition | null | undefined;
}) => {
	if (!code || code === "U")
		return <Box sx={{ mx: "auto", textAlign: "center" }}>-</Box>;

	if (code === "P")
		return (
			<Tooltip title="Passed" arrow>
				<Chip
					label={<CheckIcon sx={chipIconSx} />}
					color="success"
					size="small"
					sx={iconChipSx}
				/>
			</Tooltip>
		);

	return (
		<Tooltip title="Re-Test" arrow>
			<Chip
				label={<ReplayIcon sx={chipIconSx} />}
				color="warning"
				size="small"
				sx={iconChipSx}
			/>
		</Tooltip>
	);
};

export const ResultChip = ({ passed }: { passed: boolean | null }) => {
	if (passed === null) return <>-</>;
	return (
		<Tooltip title={passed ? "Passed" : "Failed"} arrow>
			<Chip
				label={
					passed ? <CheckIcon sx={chipIconSx} /> : <CloseIcon sx={chipIconSx} />
				}
				color={passed ? "success" : "error"}
				size="small"
				sx={iconChipSx}
			/>
		</Tooltip>
	);
};

// Date on top, time below — keeps the timestamp columns narrow.
export const TimestampCell = ({
	timestamp,
}: {
	timestamp: Date | null | undefined;
}) => {
	if (!timestamp) return <>-</>;
	return (
		<Box sx={{ lineHeight: 1.2 }}>
			<Box>{formatDate(timestamp)}</Box>
			<Box sx={{ color: "text.secondary" }}>{formatTime(timestamp)}</Box>
		</Box>
	);
};

// Same pattern as the Work Orders NotesCell: disabled icon when empty,
// enabled icon that opens a read-only modal when there is a comment.
export const CommentCell = ({
	comment,
	label,
}: {
	comment: string | null;
	label?: string | null;
}) => {
	const [showModal, setShowModal] = useState(false);
	const hasComment = !!comment && comment.trim() !== "";

	return (
		<>
			<IconButton
				onClick={() => hasComment && setShowModal(true)}
				disabled={!hasComment}
				size="small"
				sx={{ padding: "2px", cursor: hasComment ? "pointer" : "default" }}
			>
				<CommentIcon
					fontSize="small"
					color={hasComment ? "primary" : "disabled"}
				/>
			</IconButton>
			<MesfModal
				id="modal-view-heat-result-comment"
				title={`COMMENT${label ? ` - ${label}` : ""}`}
				open={showModal}
				handleClose={() => setShowModal(false)}
				maxWidth="sm"
			>
				<MesfModal.Content style={{ padding: "20px 30px" }}>
					<TextField
						label="Comment"
						variant="outlined"
						value={comment ?? ""}
						fullWidth
						multiline
						rows={4}
						disabled
						InputProps={{
							readOnly: true,
						}}
					/>
				</MesfModal.Content>
			</MesfModal>
		</>
	);
};

export const DispositionHistory = ({
	history,
}: {
	history: MechanicalTestRunDisposition[];
}) => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

	return (
		<>
			<Tooltip title="Disposition history" arrow>
				<span>
					<IconButton
						size="small"
						disabled={history.length === 0}
						onClick={(e) => setAnchorEl(e.currentTarget)}
					>
						<HistoryIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box sx={{ p: 1, maxHeight: 280, overflowY: "auto" }}>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ px: 1, fontWeight: 600 }}
					>
						Disposition history
					</Typography>
					<Table
						size="small"
						sx={{
							"& .MuiTableCell-root": {
								fontSize: 11,
								px: 1,
								whiteSpace: "nowrap",
							},
						}}
					>
						<TableHead>
							<TableRow>
								{["Result", "By", "At", "Comment"].map((label) => (
									<TableCell key={label} sx={{ color: "text.secondary" }}>
										{label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{history.map((disposition) => (
								<TableRow key={disposition.MechanicalTestRunDispositionId}>
									<TableCell>
										<StatusChip code={disposition.Disposition} />
									</TableCell>
									<TableCell>{disposedByLabel(disposition)}</TableCell>
									<TableCell>
										{formatTimestamp(disposition.DisposedAt)}
									</TableCell>
									<TableCell
										sx={{ whiteSpace: "normal", minWidth: 300, maxWidth: 500 }}
									>
										{disposition.Comment || "-"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Popover>
		</>
	);
};
